// 生字庫新版 subcontent 面板（自 WordGridModalComponent.js 抽出）
// props 驅動、不讀 wordGrid store，供 WordGrid / WordExample / WordGlyph 共用。
import React, { useState, useEffect, useContext } from 'react';
import { ExtendedContentCanvasContext, getCanvasSvg } from 'components/ExtendedContent/ExtendedContentContainer';
import { ExtendedContentCanvas } from 'components/ExtendedContent/ExtendedContentCanvas';
import WordLearningContent from 'components/WordLearningGrid/WordLearningContent';
import WordContentList from 'components/WordContentList';
import { ExtensionType } from 'constants/wordGrid';
import { ReaderZoomType } from 'constants/ReaderTools';
import { EventBus, EventBusType } from 'events/EventBus';
import { ReaderToolsEvent, CanvasEvent } from 'events/EventTypes';
import { useStore, StoreTypes } from 'context';
import AnchorAudioButton from 'components/WordExample/AnchorAudioButton';
import AudioControlsButton from 'components/common/AudioControlsButton';
import ControlsButton from 'components/common/ControlsButton';
import Icon from 'components/Icon';
import * as actionTypes from 'constants/actionTypes';
import classnames from 'classnames';
import styles from '../index.module.scss';
import Repository from 'repositories/Repository';
import { ScrollPositionType, ScrollPositionButtonStateType } from 'constants/scrollPositionType';
import { ScrollButtonStateBehavior } from 'components/ExtendedContent/ExtendedContentScrollPositionButton';
import { CELL_GAP, CELL_HEIGHT_RATIO, DEFAULT_COLS_PER_PAGE, ROWS_PER_COL, htmlToColumns, chunkPagesByCols, countCols, extractSegments, getRubyBaseText, buildEntriesFromExtension, WORDING_MAP } from './gridColumns';
import { parseChineseOnly } from 'util/zhuyin';
const { ExtendedResourceRepository } = Repository;

const EMPTY_FOUR = { meaningSet: [], wordsSet: [], idiomSet: [], glyphSet: [] };


// 注音顯示切換按鈕（顯示/隱藏時文案切換）
const RubyToggleButton = ({ isElementaryHighGrade }) => {
  const [isHidden, setIsHidden] = useState(isElementaryHighGrade ? true : false);

  useEffect(() => {
    // 容錯：若事件未帶 payload，預設不崩潰並維持現狀
    const handler = (payload = {}) => setIsHidden(!!payload.isHidden);
    EventBus.addListener({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.RubyVisibilityChanged,
      handler,
    });
    return () => {
      EventBus.removeListener({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderToolsEvent.RubyVisibilityChanged,
        handler,
      });
    };
  }, []);

  useEffect(() => {
    if (!isElementaryHighGrade) return;
    // 高年級時，延遲發送切換事件讓 ExtendedContentContainer 同步隱藏注音
    const timerId = setTimeout(() => {
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: ReaderToolsEvent.ToggleRubyVisibility,
      });
    }, 0);
    return () => clearTimeout(timerId);
  }, []);

  const onClick = () => {
    // 只廣播事件，狀態改由 RubyVisibilityChanged 同步，避免雙重 setState 造成反覆渲染
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.ToggleRubyVisibility,
    });
  };

  if (isElementaryHighGrade) return null;

  return (
    <div className={styles.rubyToggleRow}>
      <button className={styles.rubyToggleBtn} onClick={onClick} aria-label={isHidden ? '開啟注音' : '關閉注音'}>
        <img src={isHidden ? '/assets/icon/ruby_enabled.png' : '/assets/icon/ruby_disabled.png'} alt="ruby toggle" />
      </button>
    </div>
  );
};


function VerticalGridView({ html, showWordBox = true, gotoSignal, toPage, continuous = false, glyphMode = false, meaningMode = false, anchors, extensionType, word, onPlayAnchorAudio, anchorAudioSrc }) {
  const {
    canvasState,
    canvasDispatch,
    canvasWidth,
    canvasHeight,
    isContentInit,
    svgContent
  } = useContext(ExtendedContentCanvasContext) || {};
  const [pages, setPages] = React.useState([[]]);
  const [page, setPage] = React.useState(0);
  const [cellSize, setCellSize] = React.useState(32);
  const [colsPerPage, setColsPerPage] = React.useState(DEFAULT_COLS_PER_PAGE);
  const [wordItems, setWordItems] = React.useState([]);
  const [allCols, setAllCols] = React.useState([]);
  const [anchorMap, setAnchorMap] = React.useState(new Map());
  // NBK-1505：BopomofoRuby 為 @font-face 非同步載入。若在字型就緒前就 paint，
  // 字型 swap 會觸發重排，在部分 webview 上留下 ruby 殘影/裁切。
  // 這裡先 gate 顯示，等字型載完（或逾時保險）才顯示格線內容，讓第一次 paint 即為正確字型。
  const [fontReady, setFontReady] = React.useState(false);
  const [{ extendedZoomState }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  // 目前縮放倍率：放大（區間/四格/滾輪）時 scale !== 1，用來凍結 recalcCell（見下方說明）。
  const { scale: zoomScale = 1 } = extendedZoomState || {};
  const containerRef = React.useRef(null);
  const gridWrapRef = React.useRef(null);
  const pageRef = React.useRef(0);
  const prevHtmlRef = React.useRef('');
  // iOS/iPad 上 Next/Prev 事件可能在一次操作中被觸發多次（例如 mouse/touch/pointer 事件疊加）。
  // 為避免「一次點下一頁」跳兩頁（在總頁數 >= 3 時尤其明顯），
  // 我們只在 scrollPosition 從 None 切到 Next/Prev 時處理一次，直到收到 None 才允許下一次切頁。
  const lastNavRef = React.useRef(null);

  React.useEffect(() => {
    if (!html) { setPages([[]]); setAllCols([]); setPage(0); setWordItems([]); setAnchorMap(new Map()); prevHtmlRef.current = ''; return; }
    const isNewHtml = prevHtmlRef.current !== html;
    prevHtmlRef.current = html;
    const { columns } = htmlToColumns(html, { glyphMode, meaningMode });
    setAllCols(columns);
    const pgs = chunkPagesByCols(columns, colsPerPage);
    setPages(pgs);
    setWordItems([]);
    if (isNewHtml) {
      setPage(0);
      pageRef.current = 0;
    }

    const map = new Map();
    const list = Array.isArray(anchors) ? anchors : [];
    let ptr = 0;
    for (let i = 0; i < columns.length && ptr < list.length; i++) {
      const col = columns[i];
      const a = list[ptr];
      if (!a) break;
      if (a.kind === 'explain' && col && col.type === 'word' && !col.wordCont) {
        // 長標題（如 7 字成語）會被切成多欄：第一欄 word + 後續 wordCont 欄。
        // 音檔圖示要掛在「最後一欄 word」末端，而非夾在第一欄中間，
        // 且不讓 wordCont 碎片被後續 anchor 當成新詞條命中。
        let target = i;
        while (columns[target + 1] && columns[target + 1].type === 'word' && columns[target + 1].wordCont) {
          target += 1;
        }
        map.set(target, a);
        ptr += 1;
        continue;
      }
      if (a.kind === 'example' && col && col.type === 'tag' && col.sub === 'li') {
        map.set(i, a);
        ptr += 1;
        continue;
      }
    }
    setAnchorMap(map);
  }, [html, glyphMode, meaningMode, anchors ? anchors.length : 0, colsPerPage]);

  // colsPerPage 變動時，依相同 allCols 重新切頁（保留目前頁碼但做 clamp）
  React.useEffect(() => {
    if (!allCols || !allCols.length) return;
    const pgs = chunkPagesByCols(allCols, colsPerPage);
    setPages(pgs);
    setPage((p) => {
      const next = Math.max(0, Math.min((pgs.length || 1) - 1, p));
      pageRef.current = next;
      return next;
    });
  }, [colsPerPage, allCols]);

  // 分頁切換：保存當前頁畫記 -> 切換 -> 由下方 effect 載入新頁畫記
  const goToPage = React.useCallback((nextIndex) => {
    const total = (pages && pages.length) || 0;
    if (total <= 0) return;
    const clamped = Math.max(0, Math.min(total - 1, nextIndex));
    // 先保存目前頁面的畫記
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: CanvasEvent.SplitedPageSaveCanvasEvent,
      payload: { currentSplitedPageIndex: pageRef.current, extensionType, word }
    });
    // 切到新頁
    setPage(clamped);
    pageRef.current = clamped;
  }, [pages, extensionType, word]);

  // 監聽全域上下頁事件，控制內部分頁
  React.useEffect(() => {
    const handler = ({ scrollPosition }) => {
      // reset gate
      if (scrollPosition === ScrollPositionType.None) {
        lastNavRef.current = null;
        return;
      }
      // ignore duplicated same-direction events until we receive None
      if (lastNavRef.current === scrollPosition) return;
      lastNavRef.current = scrollPosition;

      if (scrollPosition === ScrollPositionType.Next) {
        goToPage(pageRef.current + 1);
      } else if (scrollPosition === ScrollPositionType.Prev) {
        goToPage(pageRef.current - 1);
      }
    };
    EventBus.addListener({ eventBusType: EventBusType.ExtendedContent, event: ReaderToolsEvent.ChangePopWindowScrollPositionEvent, handler });
    return () => {
      EventBus.removeListener({ eventBusType: EventBusType.ExtendedContent, event: ReaderToolsEvent.ChangePopWindowScrollPositionEvent, handler });
    };
  }, [goToPage]); // 移除 goToPage 依賴，改用 functional update 或 ref 避免重複綁定，但這裡先依賴 goToPage

  // 根據頁面設定底部上下頁按鈕可用狀態，並廣播頁數資訊
  React.useEffect(() => {
    const total = (pages && pages.length) || 0;
    if (!pages || total <= 1) {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.None);
    } else if (page === 0) {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.Head);
    } else if (page === total - 1) {
      ScrollButtonStateBehavior(ScrollPositionButtonStateType.Bottom);
    } else {
      ScrollButtonStateBehavior();
    }

    // 廣播目前頁數資訊供工具列顯示（1-based）
    // try {
    //   EventBus.emit({
    //     eventBusType: EventBusType.ExtendedContent,
    //     event: ReaderToolsEvent.ChangePopWindowPageInfoEvent,
    //     payload: { current: Math.min(page + 1, Math.max(total, 1)), total: Math.max(total, 1) }
    //   });
    // } catch (e) { }
    // 直接更新 store，讓 ExtendedContentScrollPositionButton 自動顯示正確頁碼
    extendedContentDispatch({
      type: actionTypes.SET_EXTENDED_CONTENT_TOTAL_PAGES,
      payload: { totalPages: Math.max(total, 1) },
    });
    extendedContentDispatch({
      type: actionTypes.SET_EXTENDED_CONTENT_CURRENT_PAGE,
      payload: { currentPage: page },
    });
  }, [page, pages, extendedContentDispatch]);

  // 外部錨點跳頁
  const prevSignal = React.useRef(gotoSignal);
  React.useEffect(() => {
    if (typeof toPage === 'number') {
      const total = (pages && pages.length) || 0;
      const clamped = Math.max(0, Math.min(total - 1, toPage));

      const isSignalChanged = gotoSignal !== prevSignal.current;
      prevSignal.current = gotoSignal;

      // 僅在「訊號改變」（使用者點擊側邊條目跳頁，selectEntryBy 會遞增 gotoSignal）時才跳頁。
      // 不可因「clamped !== pageRef.current」就跳頁：gotoPage 恆為 0，區間放大等情況會讓 recalcCell
      // 量到放大後尺寸→colsPerPage/pages 重算→觸發本 effect，若據此跳頁會把頁碼硬拉回第 0 頁，
      // 導致放大後在第一頁無法翻到下一頁、在其他頁被切回第一頁（NBK-1596）。
      if (isSignalChanged) {
        goToPage(clamped);
      }
    }
  }, [gotoSignal, toPage, pages, goToPage]);

  // 每次 page 改變時，載入該頁畫記
  React.useEffect(() => {
    // 使用 setTimeout 0 確保 DOM 更新後再觸發畫記載入，避免瞬間閃爍或畫布未準備好
    const timer = setTimeout(() => {
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageImportJSONEvent,
        payload: { currentSplitedPageIndex: page, extensionType, word }
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [page, extensionType, word]);

  // 換頁 / 切換延伸類型 / 換詞時，將縮放（四格放大、滾輪放大、區域放大）與位移歸零，
  // 避免上一頁或上一份內容的放大狀態殘留到新畫面。沿用工具列「還原」的 OriginZoom 事件，
  // 由 ExtendedContentContainer 統一把 scale/offset 重置為預設值。
  React.useEffect(() => {
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: ReaderToolsEvent.SetReaderZoomEvent,
      payload: { type: ReaderZoomType.OriginZoom },
    });
  }, [page, extensionType, word]);

  // 當頁數重算（例如切換內容）時，確保載入當前頁畫記（通常為 0）
  // React.useEffect(() => {
  //   EventBus.emit({
  //     eventBusType: EventBusType.ExtendedContent,
  //     event: CanvasEvent.SplitedPageImportJSONEvent,
  //     payload: { currentSplitedPageIndex: pageRef.current, extensionType }
  //   });
  // }, [pages.length, extensionType]);

  // Unmount 時保存畫記，以及 extensionType 改變前保存
  React.useLayoutEffect(() => {
    return () => {
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageSaveCanvasEvent,
        payload: { currentSplitedPageIndex: pageRef.current, extensionType, word }
      });
    };
  }, [extensionType, word]);

  // 動態欄數策略（依寬度決定，無上限）：
  // - ROWS_PER_COL 固定 8（高度優先）
  // - colsPerPage 依容器寬度計算能塞幾欄（依「當頁實際欄位」決定寬度倍率，避免過度保守造成左側留白）
  // - 讓超出寬度的內容「切到下一頁」，而不是裁切到畫面外

  // 推估「本份內容」最大欄寬倍率（用於穩定計算 colsPerPage，避免翻頁時 pages 邊界一直重算而卡住）
  // - normal: 1.0
  // - tag:    1.16
  // - word:   1.35
  // - image:  1.16（字音字形的圖片其實是「注音」，語意上與一行/標籤等寬，見 .glyphGrid .cellHasImage）
  const getGlobalMaxScale = React.useCallback((cols = []) => {
    let maxScale = 1.0;
    for (const c of (cols || [])) {
      if (!c) continue;
      if (c.type === 'word') maxScale = Math.max(maxScale, 1.35);
      else if (c.type === 'tag') maxScale = Math.max(maxScale, 1.16);
      // 圖片欄倍率僅字音字形（glyphMode）處理：其圖片是「注音」，渲染端 .glyphGrid .cellHasImage
      // 已縮為 tag 等寬（1.16），故估算亦以 1.16 對齊。其他 extensionType 維持原行為不受影響。
      // 注意：正則須為 \s（空白），舊版誤寫成 \\s（字面反斜線）會抓不到 <img ...>。
      if (glyphMode) {
        const items = c.items || [];
        for (const it of items) {
          if (it && typeof it.html === 'string' && /<img[\s>]/i.test(it.html)) {
            maxScale = Math.max(maxScale, 1.16);
            break;
          }
        }
      }
    }
    return maxScale;
  }, [glyphMode]);

  const recalcCell = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // 放大（區間/四格/滾輪）時 containerRef 位於 zoom 的 CSS transform 內。
    // getBoundingClientRect() 會把 transform 的放大算進去，而「還原」時 store 的 scale 會先
    // 同步變回 1、但畫面 transform 還在動畫中縮回（實測翻頁瞬間 zoomScale=1 但
    // getBoundingClientRect().width≈6300、offsetWidth 才是正確的 1197），此時量測會把
    // colsPerPage 算爆 → 重新切頁後 pages.length 變少 → clamp 把剛推進的頁碼夾回 0。
    // 為何只有部分字壞：colsPerPage 被算成 12 時，「抽」12 欄剛好併成 1 頁 → 目標頁 1 被夾回 0；
    // 「勞」14 欄仍是 2 頁 → 目標頁 1 存活。(NBK-1603 / NBK-1604，NBK-1596 延伸)
    // 解法：改用 offsetWidth/offsetHeight（版面尺寸，完全不受 CSS transform 影響），任何動畫
    // 階段都量到正確原尺寸。scale 凍結守衛保留作保險，但單靠它不夠（store scale 早於動畫歸零）。
    if (zoomScale !== 1) return;
    const rect = { width: el.offsetWidth, height: el.offsetHeight };
    const padding = 0;
    const maxRows = Math.max(ROWS_PER_COL, (showWordBox ? wordItems.length : 0));
    const colPadV = 12; // .col 垂直 padding: 6px top + 6px bottom
    // 預留一點緩衝空間 (e.g. 5px) 避免因計算誤差導致剛好溢出
    const safeBuffer = 5;
    const usableH = rect.height - padding * 2 - CELL_GAP * Math.max(0, maxRows - 1) - colPadV;
    // 依高度優先：把高度平均分成 maxRows 份，並反推單格寬度
    const sizeH = Math.floor(usableH / Math.max(1, maxRows) / CELL_HEIGHT_RATIO);

    // 依寬度決定 colsPerPage：使用「本份內容」最大倍率（但不再固定 1.5），確保穩定且不裁切
    // 同時做少量迭代，讓 cellSize / colsPerPage 收斂，避免在不同裝置上發散。
    const MIN_CELL = 8;
    let cell = Math.max(MIN_CELL, sizeH);
    let fitCols = colsPerPage || 1;
    const scale = getGlobalMaxScale(allCols);
    for (let iter = 0; iter < 3; iter++) {
      // 水平 padding = 固定 12px + 與 cell 等比的注音(<rt>)空間
      const colPadH = 12 + cell * 0.2;
      const wordBoxW = showWordBox ? (cell * 1.35 + colPadH) : 0;
      const availableW = Math.max(0, rect.width - padding * 2 - wordBoxW - safeBuffer);
      const maxColW = cell * scale + colPadH;
      fitCols = Math.max(1, Math.floor(availableW / Math.max(1, maxColW)));
      // 用每欄平均可用寬度反推 cell（以當頁最大倍率為準）
      const perCol = availableW / Math.max(1, fitCols);
      const cellFromW = Math.floor((perCol - colPadH) / Math.max(1, scale));
      const nextCell = Math.max(MIN_CELL, Math.min(cell, cellFromW));
      if (nextCell === cell) break;
      cell = nextCell;
    }
    if (!continuous && fitCols !== colsPerPage) setColsPerPage(fitCols);

    const colPadH = 12 + cell * 0.2;
    const totalCols = continuous
      ? ((pages[Math.max(0, Math.min(page, pages.length - 1))] || []).length || 0) + (showWordBox ? 1 : 0)
      : (fitCols + (showWordBox ? 1 : 0));
    const usableW = rect.width - padding * 2 - CELL_GAP * Math.max(0, totalCols - 1) - (totalCols * colPadH) - safeBuffer;
    const sizeW = continuous ? Number.MAX_SAFE_INTEGER : Math.floor(usableW / Math.max(1, totalCols));

    const next = Math.max(MIN_CELL, Math.min(cell, sizeW));
    setCellSize(next);
  }, [pages, page, wordItems, showWordBox, continuous, colsPerPage, allCols, getGlobalMaxScale, zoomScale]);

  // 以「實際渲染後的 DOM 尺寸」做二次校正：
  // - SCSS 中不同欄位（word/tag/image）有不同寬度倍率（1.35 / 1.16 / 1.5），
  //   只靠「欄數」推算會在 iPad 上出現整頁往左溢出（內容被裁切，看起來像沒切頁）。
  // - 這裡直接量測 gridWrap 的 scrollWidth/scrollHeight，若超出容器就縮小 cellSize，直到完整塞入。
  // 先前的 DOM 二次校正保留價值有限（iOS 有時 scrollWidth 回報不穩），
  // 現在改為「寬度直接決定 colsPerPage」後，理論上不應再裁切，因此移除避免抖動。

  React.useEffect(() => {
    recalcCell();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { recalcCell(); });
    ro.observe(el);
    const onWin = () => recalcCell();
    window.addEventListener('resize', onWin);
    return () => { ro.disconnect(); window.removeEventListener('resize', onWin); };
  }, [recalcCell]);

  // NBK-1505：等 BopomofoRuby 字型載完再顯示，避免字型 swap 重排造成 ruby 殘影。
  React.useEffect(() => {
    if (!(document.fonts && document.fonts.load)) { setFontReady(true); return; }
    let alive = true;
    const done = () => { if (alive) setFontReady(true); };
    // 逾時保險：字型載入失敗或過久時仍顯示，避免內容永久隱藏
    const timer = setTimeout(done, 3000);
    document.fonts.load('1em BopomofoRuby')
      .then(() => document.fonts.ready)
      .then(done)
      .catch(done);
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  const pageCols = pages[Math.max(0, Math.min(page, pages.length - 1))] || [];
  // 依裝置動態欄數：不足就補空欄（空欄放在最左側，讓內容靠右對齊）
  let renderCols = [...pageCols].reverse();

  const pageAnchorImg = renderCols.reduce((found, col) => {
    if (found) return found;
    const gIdx = allCols.indexOf(col);
    const a = anchorMap.get(gIdx);
    return (a && a.imagePath) ? a.imagePath : null;
  }, null);

  if (!continuous) {
    const pad = Math.max(0, colsPerPage - renderCols.length - (pageAnchorImg ? 1 : 0));
    if (pad > 0) {
      const empties = Array.from({ length: pad }).map(() => ({ type: 'empty', sub: null, rows: ROWS_PER_COL, items: [] }));
      renderCols = empties.concat(renderCols);
    }
  }

  if (pageAnchorImg) {
    const insertAt = Math.max(0, renderCols.length - 1);
    renderCols.splice(insertAt, 0, { type: 'anchorImage', imagePath: pageAnchorImg });
  }

  // 使含有 <span class="youtube"><a href=...> 的格子整格可點擊開啟連結
  const extractYoutubeHref = (html) => {
    if (typeof html !== 'string') return null;
    // 1) <span class="youtube"> ... <a href="...">
    const m1 = html.match(/<span[^>]*class=["'][^"']*youtube[^"']*["'][^>]*>[\s\S]*?<a[^>]*href=["']([^"']+)["']/i);
    if (m1 && m1[1]) return m1[1];
    // 2) <a class="youtube" href="...">
    const m2 = html.match(/<a[^>]*class=["'][^"']*youtube[^"']*["'][^>]*href=["']([^"']+)["']/i);
    if (m2 && m2[1]) return m2[1];
    // 2.1) 任何 <a href> 指向 youtube / youtu.be
    const m2x = html.match(/<a[^>]*href=["']([^"']+)["'][^>]*>/i);
    if (m2x && m2x[1] && /(youtube\.com|youtu\.be)/i.test(m2x[1])) return m2x[1];
    // 3) <span class="youtube" data-href="...">
    const m3 = html.match(/<span[^>]*class=["'][^"']*youtube[^"']*["'][^>]*data-href=["']([^"']+)["']/i);
    if (m3 && m3[1]) return m3[1];
    // 4) <img ... data-href="...">（僅圖片）
    const m4 = html.match(/<img[^>]*data-href=["']([^"']+)["']/i);
    if (m4 && m4[1]) return m4[1];
    // 5) 純文字內含 youtube 連結
    const m5 = html.match(/https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)[^\s"'<>]*/i);
    if (m5 && m5[0]) return m5[0];
    return null;
  };

  // 開啟外部連結：Electron 走預設瀏覽器，否則開新分頁
  const openExternalLink = (href) => {
    if (!href) return;
    if (window.electron) {
      window.electron.ipcRenderer.send("openExternal", href);
    } else {
      try { window.open(href, '_blank', 'noopener,noreferrer'); } catch (e) { }
    }
  };

  // 攔截格子內 <a> 的點擊，避免 Electron 內以新視窗開啟
  const handleCellClick = (href) => (e) => {
    const targetHref =
      (e && e.target && e.target.closest && (() => {
        const a = e.target.closest('a[href]');
        return a ? a.getAttribute('href') : null;
      })()) || href;
    if (!targetHref) return;
    if (e && e.preventDefault) e.preventDefault();
    openExternalLink(targetHref);
  };

  return (
    <div className={styles.vgRoot}>
      <div ref={containerRef} className={classnames(styles.vgContainer, { [styles.glyphGrid]: glyphMode })} style={{ '--cell': `${cellSize}px` }}>
        <div ref={gridWrapRef} className={styles.vgGridWrap} style={{ visibility: fontReady ? 'visible' : 'hidden' }}>
          {showWordBox && (
            <div className={`${styles.col} ${styles['type-word']}`}>
              {Array.from({ length: Math.max(ROWS_PER_COL, wordItems.length) }).map((_, r) => {
                const item = wordItems[r];
                const href = item ? extractYoutubeHref(item.html) : null;
                const isLastFilled = item && !wordItems[r + 1];
                return (
                  <div
                    key={`wb-${r}`}
                    className={`${styles.cell} ${item ? styles.filled : ''} ${isLastFilled ? styles.lastFilled : ''}`}
                    style={href ? { cursor: 'pointer' } : undefined}
                    onClick={handleCellClick(href)}
                    dangerouslySetInnerHTML={{ __html: item ? item.html : '' }}
                  />
                );
              })}
            </div>
          )}
          {renderCols.map((col, idx) => {
            if (col.type === 'anchorImage') {
              return (
                <div key={`img-${idx}`} className={`${styles.col} ${styles.anchorImgCol}`}>
                  <WordLearningContent imgPath={col.imagePath} />
                </div>
              );
            }
            const globalIdx = allCols.indexOf(col);
            const anchor = anchorMap.get(globalIdx);
            // 需求：每欄固定 8 格（ROWS_PER_COL），不因 word/tag 特殊樣式而改變欄高，確保整頁高度排滿。
            const rowsCount = (col.type === 'normal' && col.items.length < ROWS_PER_COL)
              ? col.items.length
              : ROWS_PER_COL;
            // 插入索引：
            // - word + explain：在 word 內容（rubies）數量之後的下一格
            // - li   + example：在標籤（第一個有內容的格）下一格
            // - 其他：預設不插入
            let insertIndex = null;
            if (anchor) {
              if (anchor.kind === 'explain' && col.type === 'word') {
                insertIndex = Math.min((col.items || []).length, rowsCount);
              } else if (anchor.kind === 'example' && col.type === 'tag' && col.sub === 'li') {
                const firstFilled = (col.items || []).findIndex(it => !!it);
                const after = firstFilled >= 0 ? firstFilled + 1 : 1;
                insertIndex = Math.min(after, rowsCount);
              }
            }
            return (
              <div key={idx} className={`${styles.col} ${col.type === 'tag' ? styles['type-tag'] : ''} ${col.type === 'word' ? styles['type-word'] : ''} ${col.sub ? styles[`sub-${col.sub}`] : ''} ${col.offset ? styles['offset'] : ''}`}>
                {Array.from({ length: rowsCount }).map((_, r) => {
                  if (anchor && insertIndex !== null && r === insertIndex) {
                    const isThisAudioPlaying = !!(anchor.audio && anchorAudioSrc === anchor.audio);
                    return (
                      <div key={r} className={`${styles.cell} ${styles.filled} ${styles.audioCell}`} style={{ writingMode: 'horizontal-tb', textOrientation: 'mixed', zIndex: '110' }}>
                        {anchor.audio && onPlayAnchorAudio ? (
                          <ControlsButton active={isThisAudioPlaying} className={styles.persistentAudioCtrl}>
                            <button className={styles.persistentAudioBtn} onClick={() => onPlayAnchorAudio(anchor.audio)}>
                              <Icon type="text" name="volumeUp" />
                            </button>
                          </ControlsButton>
                        ) : (
                          <AnchorAudioButton audioSrc={anchor.audio} hideLabel />
                        )}
                      </div>
                    );
                  }
                  const item = col.items[r];
                  const isLastFilled = item && !col.items[r + 1];
                  return (
                    <div
                      key={r}
                      className={`${styles.cell} ${item ? styles.filled : ''} ${item && item.pnClass ? item.pnClass : ''} ${item && typeof item.html === 'string' && /<img[\s>]/.test(item.html) ? styles.cellHasImage : ''} ${isLastFilled ? styles.lastFilled : ''} ${item && item.isInlineTag ? (item.tagSub === 'li' ? styles.inlineTagLi : styles.inlineTagJie) : ''}`}
                      style={{
                        ...(item && (extractYoutubeHref(item.html) || (item.tagSub && !item.isInlineTag)) ? { ...(extractYoutubeHref(item.html) ? { cursor: 'pointer' } : {}), ...(item.tagSub && !item.isInlineTag ? { background: item.tagSub === 'li' ? '#53b2fa' : '#ff7192', borderColor: item.tagSub === 'li' ? '#93c5fd' : '#ff7192', color: '#fff', borderRadius: 'calc(var(--cell) * .35)' } : {}) } : {}),
                      }}
                      onClick={handleCellClick(item ? extractYoutubeHref(item.html) : null)}
                      dangerouslySetInnerHTML={{ __html: item ? item.html : '' }}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
        {svgContent && (!canvasState || !canvasState.isActive) && (
          <div
            style={{
              inset: 0,
              zIndex: 100,
              pointerEvents: 'none',
            }}
            dangerouslySetInnerHTML={{
              __html: getCanvasSvg(svgContent),
            }}
          />
        )}
        {isContentInit && canvasState && (
          <div style={{
            inset: 0,
            zIndex: 101,
            pointerEvents: canvasState.isActive ? 'auto' : 'none',
          }}>
            <ExtendedContentCanvas
              canvasState={canvasState}
              canvasDispatch={canvasDispatch}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// 字義教學 / 字音字形：載入多段內容、合併後以直書網格呈現（純 props 版，取代原 MeaningContent）
const MeaningView = ({
  data,
  list,
  word,
  extensionType,
  bookId,
  glyphMode = false,
  meaningMode = false,
  collapsed = false,
  onToggle = () => { },
  onSelectWord = () => { },
}) => {
  const [meaningHTML, setMeaningHTML] = useState('');

  useEffect(() => {
    (async () => {
      if (!Array.isArray(data) || data.length === 0) { setMeaningHTML(''); return; }
      const htmlList = await Promise.all(
        data.map(async (item) => {
          try {
            const htmlJson = await ExtendedResourceRepository.getJSONContent({
              bookId,
              pathName: item.Content ? item.Content.split('.json')[0] : ''
            });
            const raw = htmlJson && (htmlJson.html || htmlJson.content || htmlJson.body || '');
            // 移除第一個 .word（字義教學不顯示生字標題）
            if (!raw) return '';
            const tpl = document.createElement('template');
            tpl.innerHTML = raw;
            const firstWord = tpl.content.querySelector('.word');
            if (firstWord && firstWord.parentNode) firstWord.parentNode.removeChild(firstWord);
            return tpl.innerHTML;
          } catch (e) {
            return '';
          }
        })
      );
      setMeaningHTML(htmlList.join(''));
    })();
  }, [bookId, data]);

  useEffect(() => {
    const current = list.filter(item => item.relatedWords.includes(word));
    if (current.length > 0) {
      onSelectWord({ relatedWords: current[0].relatedWords });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.content} ${styles.meaningOnly}`}>
      <div className={styles.vgLayout}>
        <div className={styles.vgGridArea}>
          <VerticalGridView html={meaningHTML} showWordBox={false} glyphMode={glyphMode} meaningMode={meaningMode} extensionType={extensionType} word={word} />
        </div>
      </div>
      <div className={styles.bottomOverlay}>
        <div className={styles.listToggleRow}>
          <button
            className={styles.listToggleBtn}
            onClick={onToggle}
          >
            {collapsed ? '展開詞彙 ▲' : '收合詞彙 ▼'}
          </button>
        </div>
        {list.length > 0 && (
          <WordContentList
            word={word}
            className={classnames(styles.wordContentList, { [styles.collapsed]: collapsed })}
            data={list}
            onChange={onSelectWord}
        />)}
      </div>
    </div>
  );
};

// 共用面板：語詞教學 / 字義教學 / 生字成語 / 字音字形 四種延伸內容。
// 由 WordGrid（受控：word/extensionType 回寫 store）、WordExample、WordGlyph（非受控：內部 state）共用。
export const WordGridSubContentPanel = ({
  allWords = [],
  bookId,
  initialWord,
  initialExtensionType = ExtensionType.WordsSet,
  controlledWord,
  onWordChange,
  controlledExtensionType,
  onExtensionTypeChange,
  isElementaryHighGrade = false,
  isZhuyin = false,
  wordsList,
  idiomList,
}) => {
  const [internalWord, setInternalWord] = useState(initialWord);
  const [internalExtensionType, setInternalExtensionType] = useState(initialExtensionType);

  const selectedWord = controlledWord !== undefined ? controlledWord : internalWord;
  const extensionType = controlledExtensionType !== undefined ? controlledExtensionType : internalExtensionType;

  const currentExtensionData = React.useMemo(() => {
    const found = allWords.find(w => w.word === selectedWord);
    return (found && found.extensionData) || EMPTY_FOUR;
  }, [allWords, selectedWord]);

  const selectWord = React.useCallback(({ word, relatedWords }) => {
    if (onWordChange) onWordChange({ word, relatedWords });
    else if (word) setInternalWord(word);
  }, [onWordChange]);

  const switchExtensionType = React.useCallback((type) => {
    if (onExtensionTypeChange) onExtensionTypeChange(type);
    else setInternalExtensionType(type);
  }, [onExtensionTypeChange]);

  const [extensionList, setExtensionList] = useState([]);
  const [entries, setEntries] = React.useState([]);
  const [combinedHTML, setCombinedHTML] = React.useState('');
  const [entryPageMap, setEntryPageMap] = React.useState({});
  const [gotoSignal, setGotoSignal] = React.useState(0);
  const [gotoPage, setGotoPage] = React.useState(0);
  const [anchors, setAnchors] = React.useState([]);
  const [isListCollapsed, setIsListCollapsed] = React.useState(true);
  const [glyphAudioPath, setGlyphAudioPath] = useState('');

  const wordRubyHtml = React.useMemo(() => {
    if (!selectedWord || !extensionList || extensionList.length === 0) return null;
    for (const ext of extensionList) {
      if (!ext || !ext.htmlJson) continue;
      const html = ext.htmlJson.html || '';
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const wordDiv = tpl.content.querySelector('.word');
      if (!wordDiv) continue;
      const rubies = wordDiv.querySelectorAll('ruby');
      for (const rb of rubies) {
        const base = getRubyBaseText(rb);
        if (base === selectedWord) return rb.outerHTML;
      }
    }
    return null;
  }, [extensionList, selectedWord]);

  useEffect(() => {
    if (extensionType !== ExtensionType.GlyphSet) {
      setGlyphAudioPath('');
      return;
    }
    const list = currentExtensionData && currentExtensionData[ExtensionType.GlyphSet];
    if (!Array.isArray(list) || list.length === 0) {
      setGlyphAudioPath('');
      return;
    }
    const first = list.find(it => it && (it.Audio || it.audio || it.Sound || it.sound));
    setGlyphAudioPath((first && (first.Audio || first.audio || first.Sound || first.sound)) || '');
  }, [extensionType, currentExtensionData]);

  // === 持久音檔：讓語詞/成語的 anchor 音檔在翻頁時不中斷 ===
  const persistentAudioRef = React.useRef(null);
  const [anchorAudioSrc, setAnchorAudioSrc] = React.useState(null);
  const [{ audioManager }, globalDispatch] = useStore(StoreTypes.global);
  // NBK-1512：字音字形分頁後，僅第 1 頁顯示喇叭。頁碼由 VerticalGridView 寫入 extendedContent store。
  const [{ currentPage: extendedCurrentPage }] = useStore(StoreTypes.extendedContent);

  const playAnchorAudio = React.useCallback((audioPath) => {
    const audio = persistentAudioRef.current;
    if (!audio) return;

    const resolvedSrc = ExtendedResourceRepository.getDataSrc({ bookId, pathName: audioPath });

    if (anchorAudioSrc === audioPath && !audio.paused) {
      audio.pause();
      globalDispatch({ type: actionTypes.CLEAR_CURRENT_PLAYING_AUDIO });
      setAnchorAudioSrc(null);
      return;
    }

    if (audioManager.currentPlayingAudioElement &&
        audioManager.currentPlayingAudioElement !== audio) {
      audioManager.currentPlayingAudioElement.currentTime = 0;
      audioManager.currentPlayingAudioElement.pause();
      audioManager.currentPlayingAudioElement.dispatchEvent(new CustomEvent('ended', {
        detail: { forced: true }
      }));
    }

    audio.src = resolvedSrc;
    globalDispatch({
      type: actionTypes.SET_CURRENT_PLAYING_AUDIO,
      payload: { audioElement: audio }
    });
    setAnchorAudioSrc(audioPath);
    audio.play();
  }, [anchorAudioSrc, audioManager.currentPlayingAudioElement, bookId, globalDispatch]);

  useEffect(() => {
    const audio = persistentAudioRef.current;
    if (!audio) return;
    const onEnded = () => setAnchorAudioSrc(null);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  useEffect(() => {
    const audio = persistentAudioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      audio.currentTime = 0;
      globalDispatch({ type: actionTypes.CLEAR_CURRENT_PLAYING_AUDIO });
    }
    setAnchorAudioSrc(null);
  }, [selectedWord, extensionType, globalDispatch]);

  useEffect(() => {
    (async () => {
      let extensionArray = currentExtensionData[extensionType] || [];
      const wordPromise = [];
      extensionArray.forEach((word, index) => {
        wordPromise.push(new Promise(async (resolve, reject) => {
          const htmlJson = await ExtendedResourceRepository.getJSONContent({
            bookId,
            pathName: word.Content ? word.Content.split(".json")[0] : "",
          });
          if (htmlJson) {
            resolve({ htmlJson, index, type: word.type });
          }
        }));
      });

      await Promise.all(wordPromise).then(result => {
        setExtensionList(result);
      });
    })();
  }, [bookId, currentExtensionData, extensionType]);

  // 建立側邊條目（依 extensionList 與 currentExtensionData）
  useEffect(() => {
    if (!extensionList || extensionList.length === 0) { setEntries([]); return; }
    const dataArr = currentExtensionData && currentExtensionData[extensionType] ? currentExtensionData[extensionType] : [];
    const built = buildEntriesFromExtension(extensionList, extensionType, dataArr);
    setEntries(built);
  }, [extensionList, extensionType, currentExtensionData]);

  // 串聯所有條目的 HTML，並記錄每個條目起始頁
  useEffect(() => {
    const dataArr = currentExtensionData && currentExtensionData[extensionType] ? currentExtensionData[extensionType] : [];
    if (!extensionList || extensionList.length === 0 || !Array.isArray(dataArr)) { setAnchors([]); return; }
    const seq = [];
    dataArr.forEach((item, index) => {
      const ext = extensionList.find(e => e.index === index && e.htmlJson);
      if (!ext || !ext.htmlJson) return;
      const html = ext.htmlJson.html || ext.htmlJson.content || ext.htmlJson.body || '';
      const seg = extractSegments(html, { includeWord: extensionType === 'wordsSet' });
      const imgPath = item.ImagePath || item.Path || '';
      // 一個詞條只有一張圖，imagePath 只掛在該詞條的第一筆 anchor 上，
      // 避免解/例被分到不同頁時，兩頁各自命中同一張圖而重複顯示。
      // 音檔（解=CommentAudio、例=ExampleAudio）為兩段不同內容，仍各自保留一筆。
      let imgAssigned = false;
      if (seg.hasExplain) {
        seq.push({ kind: 'explain', audio: item.CommentAudio || '', imagePath: imgAssigned ? '' : imgPath });
        imgAssigned = true;
      }
      if (seg.hasExample) {
        seq.push({ kind: 'example', audio: item.ExampleAudio || '', imagePath: imgAssigned ? '' : imgPath });
        imgAssigned = true;
      }
    });
    setAnchors(seq);
  }, [extensionList, extensionType, currentExtensionData]);

  useEffect(() => {
    if (!entries || entries.length === 0) { setCombinedHTML(''); setEntryPageMap({}); return; }
    let acc = 0;
    const meta = [];
    const out = [];
    entries.forEach(e => {
      meta.push({ key: `${e.kind}:${e.extIndex}`, startCol: acc });
      const c = countCols(e.html);
      acc += c;
      out.push(e.html);
    });
    const combined = out.join('');
    try {
      const { columns } = htmlToColumns(combined);
      const pgs = chunkPagesByCols(columns);
      let cursor = 0;
      const pageStarts = (pgs || []).map(pg => {
        const s = cursor;
        cursor += (pg && pg.length) || 0;
        return s;
      });
      const findPageIndex = (colIdx) => {
        if (!pageStarts.length) return 0;
        for (let p = 0; p < pageStarts.length; p++) {
          const s = pageStarts[p];
          const e = (p + 1 < pageStarts.length) ? pageStarts[p + 1] : Number.POSITIVE_INFINITY;
          if (colIdx >= s && colIdx < e) return p;
        }
        return 0;
      };
      const pageMap = {};
      meta.forEach(m => { pageMap[m.key] = findPageIndex(m.startCol); });
      setEntryPageMap(pageMap);
    } catch (e) {
      let acc2 = 0; const pageMap = {};
      entries.forEach(e2 => {
        pageMap[`${e2.kind}:${e2.extIndex}`] = Math.floor(acc2 / DEFAULT_COLS_PER_PAGE);
        acc2 += countCols(e2.html);
      });
      setEntryPageMap(pageMap);
    }
    setCombinedHTML(combined);
  }, [entries]);

  const currentHTML = combinedHTML
  // eslint-disable-next-line no-unused-vars
  const selectEntryBy = (kind, extIndex) => {
    const p = entryPageMap[`${kind}:${extIndex}`];
    if (typeof p === 'number') {
      setGotoPage(p);
      setGotoSignal(s => s + 1);
    }
  };

  const deriveList = (key) => allWords
    .filter(w => (w.extensionData[key] || []).length > 0)
    .map(w => ({ label: w.word, value: w.word, relatedWords: [w.word] }));
  const meaningList = deriveList(ExtensionType.MeaningSet);
  const glyphList = deriveList(ExtensionType.GlyphSet);
  const wordsSetList = wordsList || deriveList(ExtensionType.WordsSet);
  const idiomSetList = idiomList || deriveList(ExtensionType.IdiomSet);

  return (
    <div className={styles.wordGridConentContainer}>
      <audio ref={persistentAudioRef} playsInline crossOrigin="anonymous" />
      {
        extensionType === ExtensionType.MeaningSet && (
          <MeaningView
            data={currentExtensionData[ExtensionType.MeaningSet]}
            list={meaningList}
            word={selectedWord}
            extensionType={extensionType}
            bookId={bookId}
            meaningMode
            collapsed={isListCollapsed}
            onToggle={() => setIsListCollapsed(v => !v)}
            onSelectWord={selectWord}
          />
        )
      }
      <div className = {styles.setup}>
      {(controlledExtensionType && extensionType !== ExtensionType.WordsSet) && <div className={styles.currentWordArea}>
          <div
            className={classnames(styles.currentWordArea, {
              [styles.currentWordAreaWithAudio]:
                extensionType === ExtensionType.GlyphSet && !!glyphAudioPath,
            })}
          >
            <div
              className={styles.currentWordBadge}
              dangerouslySetInnerHTML={{ __html: wordRubyHtml || selectedWord }}
            />
            {extensionType === ExtensionType.GlyphSet && !!glyphAudioPath && (
              // 保持掛載讓音檔延續播放不中斷；第 1 頁之後僅以 CSS 隱藏喇叭。
              <div className={classnames(styles.currentWordAudio, { [styles.hidden]: extendedCurrentPage !== 0 })}>
                <AudioControlsButton
                  src={ExtendedResourceRepository.getDataSrc({
                    bookId,
                    pathName: glyphAudioPath,
                  })}
                />
              </div>
            )}
          </div>
      </div>}
      <RubyToggleButton isElementaryHighGrade={isElementaryHighGrade} />
        {controlledExtensionType && <div className={classnames(styles.controls, {
          [styles.notWordsSet]: extensionType !== 'wordsSet'
        })}>
          <div className={styles.extensionButtons}>
            {
              Object.values(ExtensionType).map(item => {
                const isDisabled = currentExtensionData[item] && currentExtensionData[item].length < 1;
                return (
                  <div key={item} className={classnames(styles.button,
                    {
                      [styles.active]: item === extensionType,
                      [styles.disabled]: isDisabled
                    })
                  }
                    onClick={() => {
                      !isDisabled && switchExtensionType(item)
                    }}>
                    {WORDING_MAP[item]}
                  </div>
                )
              })
            }
          </div>
        </div>}
      </div>
      {
        (extensionType === ExtensionType.WordsSet || extensionType === ExtensionType.IdiomSet) && (
          <div className={styles.content}>
            <div className={styles.vgLayout}>
              <div className={styles.vgGridArea}>
                <VerticalGridView html={currentHTML} showWordBox={false} gotoSignal={gotoSignal} toPage={gotoPage} anchors={anchors} extensionType={extensionType} word={selectedWord} onPlayAnchorAudio={playAnchorAudio} anchorAudioSrc={anchorAudioSrc} />
              </div>
            </div>
            <div className={styles.bottomOverlay}>
              <div className={styles.listToggleRow}>
                <button
                  className={styles.listToggleBtn}
                  onClick={() => setIsListCollapsed(v => !v)}
                >
                  {isListCollapsed ? '展開詞彙 ▲' : '收合詞彙 ▼'}
                </button>
              </div>
              <WordContentList
                word={selectedWord}
                zhuyin={isZhuyin}
                className={classnames(styles.wordContentList, { [styles.collapsed]: isListCollapsed })}
                data={extensionType === ExtensionType.WordsSet ? wordsSetList : idiomSetList}
                onChange={selectWord}
              />
            </div>
          </div>
        )
      }
      {
        extensionType === ExtensionType.GlyphSet && (
          <MeaningView
            data={currentExtensionData[ExtensionType.GlyphSet]}
            list={glyphList}
            word={selectedWord}
            extensionType={extensionType}
            bookId={bookId}
            glyphMode={true}
            collapsed={isListCollapsed}
            onToggle={() => setIsListCollapsed(v => !v)}
            onSelectWord={selectWord}
          />
        )
      }
    </div>
  );
};
